package dev.lingvo.site.pages.app.projects

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.COMMON_LOCALES
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * A titled block of fields.
 *
 * The heading and its sentence were written by hand here — an `H2` and a `P` with their own type
 * decisions — beside a design system whose `LSectionHeading` is that exact pair. It also carries
 * the glyph slot every other section header in the product now uses, which is what makes a form of
 * two blocks scannable rather than two identical walls of label text.
 */
@Composable
internal fun FormSection(
    title: String,
    description: String,
    icon: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        LSectionHeading(title = title, description = description, icon = icon)
        content()
    }
}

@Composable
internal fun SlugPreview(slug: String) {
    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        Span(
            Modifier
                .color(ColorMode.current.toSitePalette().textSecondary)
                .fontSize(TypeScale.Micro)
                .fontWeight(600)
                .styleModifier {
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                }
                .toAttrs(),
        ) {
            Text(t("app.projects.project_creation_fields.project_url_approximate", "Project URL (approximate)"))
        }
        Div(
            Modifier
                .fillMaxWidth()
                .backgroundColor(ColorMode.current.toSitePalette().surfaceSunken)
                .border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().border)
                .borderRadius(Radius.Control)
                .padding(Space.MD)
                .toAttrs(),
        ) {
            Span(
                Modifier
                    .monoFont()
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .toAttrs(),
            ) {
                // A route, not a sentence. This is the address the project will actually have,
                // rendered in mono beside the field that decides it, and a translator who
                // "localises" the path segments produces a preview of a URL that does not exist.
                // The one piece of it that varies is already interpolated. i18n-ignore
                Text("/app/projects/$slug")
            }
        }
        Span(
            Modifier
                .color(ColorMode.current.toSitePalette().textSecondary)
                .fontSize(TypeScale.Micro)
                .toAttrs(),
        ) {
            Text(t("app.projects.project_creation_fields.a_number_is_added_if_this", "A number is added if this address is already taken."))
        }
    }
}

@Composable
internal fun TargetLocalePicker(
    sourceLocale: String,
    selected: Set<String>,
    search: String,
    enabled: Boolean,
    showError: Boolean,
    onSearchChange: (String) -> Unit,
    onSelectedChange: (Set<String>) -> Unit,
) {
    val query = search.trim()
    val visibleLocales = COMMON_LOCALES.filter { locale ->
        query.isBlank() ||
            locale.code.contains(query, ignoreCase = true) ||
            locale.englishName.contains(query, ignoreCase = true) ||
            locale.nativeName.contains(query, ignoreCase = true)
    }
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        Row(
            Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Micro)
                    .fontWeight(600)
                    .styleModifier {
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) {
                Text(t("app.projects.project_creation_fields.target_locales", "Target languages"))
            }
            LBadge(tPlural(
                "app.projects.project_creation_fields.n_selected",
                selected.size,
                "{count} selected",
                "{count} selected",
            ), variant = LBadgeVariant.ACCENT)
        }
        if (selected.isNotEmpty()) {
            Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
                COMMON_LOCALES.filter { it.code in selected }.forEach { locale ->
                    LChip(
                        text = "${localeFlagPrefix(locale.code)}${locale.code}",
                        onRemove = if (enabled) {
                            { onSelectedChange(selected - locale.code) }
                        } else {
                            null
                        },
                        removeLabel = trf(
                            "app.projects.project_creation_fields.remove_language",
                            "Remove {language}",
                            "language" to locale.englishName,
                        ),
                    )
                }
            }
        }
        Column(
            Modifier
                .fillMaxWidth()
                .backgroundColor(ColorMode.current.toSitePalette().surfaceSunken)
                .border(
                    1.px,
                    LineStyle.Solid,
                    if (showError) {
                        ColorMode.current.toSitePalette().danger
                    } else {
                        ColorMode.current.toSitePalette().border
                    },
                )
                .borderRadius(Radius.Control)
                .padding(Space.MD)
                .gap(Space.MD),
        ) {
            LSearchField(
                value = search,
                onValueChange = onSearchChange,
                label = t("app.projects.project_creation_fields.search_target_locales", "Search target languages"),
                placeholder = t("app.projects.project_creation_fields.search_language_or_locale_code", "Search language or locale code"),
                enabled = enabled,
                shortcutHint = null,
            )
            Column(Modifier.fillMaxWidth().maxHeight(280.px).overflow { y(Overflow.Auto) }.gap(Space.MD)) {
                visibleLocales.forEach { locale ->
                    val isSource = locale.code == sourceLocale
                    LCheckbox(
                        checked = locale.code in selected,
                        onCheckedChange = { checked ->
                            if (!isSource) {
                                onSelectedChange(
                                    if (checked) selected + locale.code else selected - locale.code,
                                )
                            }
                        },
                        label = "${localeFlagPrefix(locale.code)}${locale.englishName} · ${locale.code}" +  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                            if (isSource) {
                                // The one word in this label a translator can change, so it is the
                                // one that goes through the catalogue. It read " (source)" in
                                // every language, on the checkbox that explains why one row of the
                                // list cannot be picked.
                                " " + t("app.projects.project_creation_fields.source_suffix", "(source)")
                            } else {
                                ""
                            },
                        enabled = enabled && !isSource,
                    )
                }
                if (visibleLocales.isEmpty()) {
                    LEmptyState(
                        title = t("app.projects.project_creation_fields.no_locales_found", "No locales found"),
                        description = t("app.projects.project_creation_fields.try_another_language_name_or_locale", "Try another language name or locale code."),
                        modifier = Modifier.fillMaxWidth(),
                        icon = // SEARCH, not HELP. Every other "your query matched nothing" state in the product
                    // uses the magnifier; the question mark reads as "we cannot help you", which is
                    // a different and worse thing to say about a typo.
                    LEmptyStateIcon.SEARCH,
                    ) {
                        LButton(
                            label = t("app.projects.project_creation_fields.clear_search", "Clear search"),
                            onClick = { onSearchChange("") },
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                            enabled = enabled,
                        )
                    }
                }
            }
        }
        if (showError) {
            P(
                Modifier
                    .color(ColorMode.current.toSitePalette().danger)
                    .fontSize(TypeScale.Small)
                    .margin(0.px)
                    .toAttrs { attr("role", "alert") },
            ) {
                Text(t("app.projects.project_creation_fields.choose_at_least_one_target_locale", "Choose at least one target language"))
            }
        }
    }
}

internal fun projectSlug(name: String): String = name
    .lowercase()
    .replace(Regex("[^a-z0-9]+"), "-")
    .trim('-')
    .take(64)
    .trimEnd('-')
    .ifBlank { "project" }
